// Ace Editor bridge — ported from ca/js/ace-bridge.js into BSEE.
//
// Lazy-loads Ace from CDN the first time a `.js-ace-editor` element is found
// on the page, then initializes editors. Kept as a CDN load (not bundled) to
// keep biz-bs-ee.js small — Ace's full mode set is ~1MB and we don't want it
// on every page just because BSEE ships everywhere.
//
// Docs: https://ace.c9.io/ and _README/code-editing.readme.md
//
// Usage:
//   <div id="code-display"
//        class="js-ace-editor"
//        data-mode="markdown"
//        data-readonly="true"
//        data-content="…"></div>
//
// Auto-init runs on DOMContentLoaded. To init programmatically:
//   window.initAceEditor('code-display', { mode: 'markdown' });

const ACE_CDN = 'https://cdnjs.cloudflare.com/ajax/libs/ace/1.32.2';

declare global {
    interface Window {
        ace?: any;
        initAceEditor?: (elementId: string, options?: AceOptions) => Promise<any>;
    }
}

interface AceOptions {
    mode?: string;
    theme?: string;
    readOnly?: boolean;
    fontSize?: number | string;
    tabSize?: number | string;
    autoHeight?: boolean;
}

let acePromise: Promise<any> | null = null;

function loadAceFromCDN(): Promise<any> {
    if (window.ace) {
        return Promise.resolve(window.ace);
    }
    if (acePromise) {
        return acePromise;
    }
    acePromise = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = `${ACE_CDN}/ace.min.js`;
        script.onload = () => {
            if (window.ace) {
                resolve(window.ace);
            } else {
                reject(new Error('Ace failed to load'));
            }
        };
        script.onerror = () => reject(new Error('Failed to load Ace from CDN'));
        document.head.appendChild(script);
    });
    return acePromise;
}

export async function initAceEditor(elementId: string, options: AceOptions = {}): Promise<any> {
    try {
        const ace = await loadAceFromCDN();

        ace.config.set('basePath', ACE_CDN);
        ace.config.set('modePath', ACE_CDN);
        ace.config.set('themePath', ACE_CDN);
        ace.config.set('workerPath', ACE_CDN);

        const element = document.getElementById(elementId);
        if (!element) {
            console.error(`Element #${elementId} not found`);
            return;
        }

        // Read the seed content BEFORE ace.edit(): afterwards innerHTML is Ace's own markup,
        // so an empty data-content (new item, empty field) would load that markup as the value.
        const content = element.dataset.content ?? element.innerHTML;

        const editor = ace.edit(elementId);

        const mode = element.dataset.mode || options.mode || 'javascript';
        editor.session.setMode(`ace/mode/${mode}`);

        const theme = element.dataset.theme || options.theme || 'ace/theme/chrome';
        editor.setTheme(theme);

        const readOnly = element.dataset.readonly === 'true' || options.readOnly === true;
        editor.setReadOnly(readOnly);

        editor.setOptions({
            fontSize: element.dataset.fontSize || options.fontSize || 12,
            showPrintMargin: element.dataset.printMargin !== 'true',
            highlightActiveLine: !readOnly,
            showLineNumbers: true,
            tabSize: element.dataset.tabSize || options.tabSize || 2,
        });

        if (content) {
            editor.setValue(content, -1);
        }

        if (element.dataset.autoHeight === 'true' || options.autoHeight === true) {
            const updateHeight = () => {
                const lineHeight = editor.renderer.lineHeight;
                const lineCount = editor.session.getLength();
                const newHeight = Math.max(200, lineCount * lineHeight + 25);
                element.style.height = `${newHeight}px`;
                editor.resize();
            };
            setTimeout(updateHeight, 100);
            editor.session.on('change', updateHeight);
        }

        return editor;
    } catch (error) {
        console.error('Failed to initialize Ace editor:', error);
    }
}

window.initAceEditor = initAceEditor;

// ═══════════════════════════════════════════════════════════════════════════
// FORM CODE FIELDS — <textarea data-bsee-code="html">
// bseeform's textarea(…, ['code' => 'html']) emits this. The textarea stays the
// field that POSTs; it's hidden and an Ace editor is mounted after it, writing
// every change back into it. Height follows the textarea's rows.
// ═══════════════════════════════════════════════════════════════════════════
const CODE_LINE_HEIGHT_PX = 18;
// Dark theme for editable form fields (owner liked Cobalt and Dracula; Dracula made
// tags hard to tell apart). Override per field with data-bsee-code-theme="dracula".
const CODE_FIELD_THEME = 'cobalt';
// Ace colours every HTML tag name the same, so h1–h6 don't stand out from <p>/<div>.
// In html mode, lines holding a heading tag get a full-line marker (.bsee-ace-heading,
// styled in _forms.scss) — theme-independent and cheap to recompute on each change.
const HEADING_TAG = /<\/?h[1-6][\s>]/i;

function markHeadingLines(editor: any): void {
    const session = editor.session;
    const Range = window.ace.require('ace/range').Range;
    const markerIds: number[] = [];
    const refresh = () => {
        markerIds.splice(0).forEach((id) => session.removeMarker(id));
        const lineCount = session.getLength();
        for (let row = 0; row < lineCount; row++) {
            if (HEADING_TAG.test(session.getLine(row))) {
                markerIds.push(session.addMarker(new Range(row, 0, row, 1), 'bsee-ace-heading', 'fullLine'));
            }
        }
    };
    refresh();
    session.on('change', refresh);
}

async function initCodeTextarea(textarea: HTMLTextAreaElement): Promise<void> {
    const holder = document.createElement('div');
    holder.id = `${textarea.id || textarea.name}-ace`;
    holder.className = 'border rounded';
    holder.style.height = `${Math.max(textarea.rows, 10) * CODE_LINE_HEIGHT_PX}px`;
    holder.dataset.mode = textarea.dataset.bseeCode || 'html';
    holder.dataset.theme = `ace/theme/${textarea.dataset.bseeCodeTheme || CODE_FIELD_THEME}`;
    holder.dataset.content = textarea.value;
    textarea.insertAdjacentElement('afterend', holder);

    const editor = await initAceEditor(holder.id, { fontSize: 13 });
    if (!editor) {
        return; // Ace failed to load — leave the plain textarea visible and usable.
    }
    textarea.classList.add('d-none');
    editor.setOption('wrap', true);
    if (holder.dataset.mode === 'html') {
        markHeadingLines(editor);
    }
    editor.session.on('change', () => {
        textarea.value = editor.getValue();
    });
}

document.addEventListener('DOMContentLoaded', async () => {
    const editors = document.querySelectorAll<HTMLElement>('.js-ace-editor[id]');
    for (const element of editors) {
        await initAceEditor(element.id);
    }
    const codeFields = document.querySelectorAll<HTMLTextAreaElement>('textarea[data-bsee-code]');
    for (const textarea of codeFields) {
        await initCodeTextarea(textarea);
    }
});
